/**
 * Utility for client-side image compression and CDN URL optimization for book covers/thumbnails.
 */

export interface CompressionOptions {
  maxWidth?: number;
  maxHeight?: number;
  quality?: number; // 0.1 to 1.0
  mimeType?: string; // e.g. 'image/webp' or 'image/jpeg'
}

/**
 * Optimizes external image URLs (e.g. Unsplash) with CDN parameters for width and quality
 */
export function getOptimizedImageUrl(
  url: string | undefined | null,
  targetWidth: number = 300,
  quality: number = 75
): string {
  if (!url || typeof url !== 'string') return '';

  const trimmed = url.trim();
  if (!trimmed) return '';

  // Handle Unsplash images
  if (trimmed.includes('images.unsplash.com')) {
    try {
      const parsedUrl = new URL(trimmed);
      parsedUrl.searchParams.set('w', targetWidth.toString());
      parsedUrl.searchParams.set('q', quality.toString());
      parsedUrl.searchParams.set('auto', 'format'); // auto webp/avif
      parsedUrl.searchParams.set('fit', 'crop');
      return parsedUrl.toString();
    } catch {
      return trimmed;
    }
  }

  // Return original URL for other sources
  return trimmed;
}

/**
 * Converts any image (File, Blob, or Data URL) to a high-quality WebP format on the client side.
 * This ensures all user-uploaded images (like book covers) are uniform, optimized, and 
 * performant before they reach local storage (Dexie) or cloud storage (R2).
 * 
 * @param fileOrDataUrl - The source image to convert
 * @param maxWidth - The maximum width for resizing (preserving aspect ratio)
 * @param quality - Compression quality (0.0 to 1.0)
 */
export async function convertToWebP(
  fileOrDataUrl: File | Blob | string,
  maxWidth = 1000,
  quality = 0.82
): Promise<{ blob: Blob; dataUrl: string; sizeKB: number; width: number; height: number }> {
  // Use the existing compression utility which already handles canvas-based conversion
  const result = await compressImageFile(fileOrDataUrl, {
    maxWidth,
    maxHeight: Math.round(maxWidth * 1.5), // Common book aspect ratio
    quality,
    mimeType: 'image/webp'
  });

  return {
    blob: result.blob,
    dataUrl: result.compressedDataUrl,
    sizeKB: result.compressedSizeKB,
    width: result.width,
    height: result.height,
  };
}

/**
 * Helper to convert a data URL (base64) to a Blob
 */
export function dataURLToBlob(dataUrl: string): Blob {
  const arr = dataUrl.split(',');
  const mimeMatch = arr[0].match(/:(.*?);/);
  const mime = mimeMatch ? mimeMatch[1] : 'image/webp';
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new Blob([u8arr], { type: mime });
}

/**
 * Compresses an image File or Data URL using HTML Canvas to reduce file size for thumbnails.
 */
export async function compressImageFile(
  fileOrDataUrl: File | Blob | string,
  options: CompressionOptions = {}
): Promise<{
  compressedDataUrl: string;
  originalSizeKB: number;
  compressedSizeKB: number;
  savingsPercentage: number;
  width: number;
  height: number;
  blob: Blob;
}> {
  const maxWidth = options.maxWidth || 400;
  const maxHeight = options.maxHeight || 600;
  const quality = options.quality || 0.78;
  const mimeType = options.mimeType || 'image/webp';

  return new Promise((resolve, reject) => {
    let originalSizeKB = 0;

    const img = new Image();
    img.crossOrigin = 'anonymous';

    const handleImageLoad = async () => {
      try {
        // Modern browsers: wait for the image to be fully decoded before accessing dimensions
        if ('decode' in img) {
          try {
            await img.decode();
          } catch (e) {
            console.warn('img.decode() failed, falling back to traditional load', e);
          }
        }

        let { naturalWidth: width, naturalHeight: height } = img;

        // If dimensions are still 0, something is wrong with the image data
        if (width === 0 || height === 0) {
          reject(new Error('Gambar tidak memiliki dimensi yang valid atau gagal didekode oleh peramban.'));
          return;
        }

        // Calculate aspect ratio preserving scale
        if (width > maxWidth || height > maxHeight) {
          const ratio = Math.min(maxWidth / width, maxHeight / height);
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Canvas context non-available'));
          return;
        }

        // High quality smoothing
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, 0, 0, width, height);

        // Try outputting webp, fallback to jpeg if unsupported
        canvas.toBlob((blob) => {
          if (!blob) {
            reject(new Error('Gagal membuat blob gambar'));
            return;
          }

          const reader = new FileReader();
          reader.onloadend = () => {
            const compressedDataUrl = reader.result as string;
            const compressedSizeKB = Math.round(blob.size / 1024) || 1; // At least 1KB if not 0

            if (originalSizeKB === 0) {
              originalSizeKB = compressedSizeKB * 2;
            }

            const savingsPercentage = originalSizeKB > 0
              ? Math.max(0, Math.round(((originalSizeKB - compressedSizeKB) / originalSizeKB) * 100))
              : 0;

            resolve({
              compressedDataUrl,
              originalSizeKB,
              compressedSizeKB,
              savingsPercentage,
              width,
              height,
              blob,
            });
          };
          reader.readAsDataURL(blob);
        }, mimeType, quality);
      } catch (err) {
        reject(err);
      }
    };

    img.onerror = () => {
      reject(new Error('Gagal memuat file gambar untuk kompresi'));
    };

    img.onload = handleImageLoad;

    if (fileOrDataUrl instanceof File || fileOrDataUrl instanceof Blob) {
      originalSizeKB = Math.round((fileOrDataUrl as Blob).size / 1024);
      const reader = new FileReader();
      reader.onload = (e) => {
        if (e.target?.result) {
          img.src = e.target.result as string;
        }
      };
      reader.onerror = () => reject(new Error('Gagal membaca file untuk pratinjau'));
      reader.readAsDataURL(fileOrDataUrl as Blob);
    } else {
      originalSizeKB = Math.round((fileOrDataUrl.length * 0.75) / 1024);
      img.src = fileOrDataUrl;
    }
  });
}

/**
 * Resolves the Cloudflare Worker API base URL for image proxying across environments:
 * - In local dev: empty string (or VITE_API_URL)
 * - In production (Cloudflare Pages or custom domain): points to the Worker backend
 */
export function getWorkerApiBase(): string {
  let envApi = '';
  try {
    if (typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_API_URL) {
      envApi = String((import.meta as any).env.VITE_API_URL).trim();
    }
  } catch {}

  if (envApi) return envApi.replace(/\/+$/, '');

  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    if (host.includes('pages.dev') || host.includes('uibn.web.id')) {
      return 'https://api.uibn.web.id';
    }
  }
  return '';
}

/**
 * Robustly resolves book cover image source, handling string URLs, Blob/File objects,
 * Dexie cover objects, or nested object payloads with URL/dataUrl/blob.
 */
export function resolveCoverUrl(coverUrl: any): string {
  if (!coverUrl) return '';

  let rawUrl = '';

  // If it's a string URL
  if (typeof coverUrl === 'string') {
    rawUrl = coverUrl.trim();
  } else if (coverUrl instanceof Blob || coverUrl instanceof File) {
    try {
      return URL.createObjectURL(coverUrl);
    } catch {
      return '';
    }
  } else if (typeof coverUrl === 'object') {
    if (typeof coverUrl.dataUrl === 'string' && coverUrl.dataUrl) {
      rawUrl = coverUrl.dataUrl.trim();
    } else if (typeof coverUrl.url === 'string' && coverUrl.url) {
      rawUrl = coverUrl.url.trim();
    } else if (typeof coverUrl.coverUrl === 'string' && coverUrl.coverUrl) {
      rawUrl = coverUrl.coverUrl.trim();
    } else if (coverUrl.blob instanceof Blob) {
      try {
        return URL.createObjectURL(coverUrl.blob);
      } catch {
        return '';
      }
    }
  }

  if (!rawUrl) return '';

  // If it's a data: or blob: URL, return as-is
  if (rawUrl.startsWith('data:') || rawUrl.startsWith('blob:')) {
    return rawUrl;
  }

  // If it's already an active custom CDN domain URL, return as-is
  if (rawUrl.startsWith('https://cdn.uibn.web.id')) {
    return rawUrl;
  }

  // If it's a relative /api/images/ route, prepend Worker API base so Cloudflare Pages doesn't return index.html
  if (rawUrl.startsWith('/api/images/')) {
    const apiBase = getWorkerApiBase();
    return `${apiBase}${rawUrl}`;
  }

  // If it's a Cloudflare R2 development URL (e.g. pub-*.r2.dev) or legacy S3 URL, redirect to custom domain CDN https://cdn.uibn.web.id
  if (
    rawUrl.startsWith('https://pub-') ||
    rawUrl.includes('.r2.dev') ||
    rawUrl.includes('.r2.cloudflarestorage.com') ||
    rawUrl.includes('/api/books/proxy-cover/')
  ) {
    try {
      const parsed = new URL(rawUrl, typeof window !== 'undefined' ? window.location.origin : 'http://localhost:3000');
      const segments = parsed.pathname.split('/').filter(Boolean);
      if (segments.length > 0) {
        const key = segments[segments.length - 1];
        return `https://cdn.uibn.web.id/${encodeURIComponent(key)}`;
      }
    } catch {
      // fallback
    }
  }

  // If it's a raw R2 filename/key without scheme or path (e.g. 1790404173300-fcxv1emk-1000072342.png)
  if (!rawUrl.startsWith('http://') && !rawUrl.startsWith('https://') && !rawUrl.startsWith('/') && rawUrl.includes('.')) {
    return `https://cdn.uibn.web.id/${encodeURIComponent(rawUrl)}`;
  }

  return rawUrl;
}
